import { config } from "../config";
import { authorizationHeaders, clearSession } from "./admin";
import { ApiError, apiErrorCode } from "./api-client";
import {
  downloadFilename,
  mediaType,
} from "./boundaries/http-download.protocol";

type DownloadResponse = { blob: Blob; filename: string };

/** What the caller expects back, so this transport knows no file format. */
type ExpectedDownload = { contentType: string; extension: string };

/**
 * Fetch a binary attachment and verify it is what the caller asked for.
 *
 * Format knowledge lives at the call site, not here: a CSV or PDF export reuses
 * this unchanged. Everything throws `ApiError` so `errorMessage` can unwrap it
 * the same way it unwraps a JSON failure.
 */
export async function requestDownload(
  path: string,
  expected: ExpectedDownload,
  signal?: AbortSignal,
): Promise<DownloadResponse> {
  const response = await fetch(config.apiBaseUrl + path, {
    signal,
    headers: authorizationHeaders(),
  });
  if (!response.ok) {
    const body = await response.text();
    if (response.status === 401) clearSession();
    throw new ApiError(response.status, body, apiErrorCode(body));
  }
  const contentType = mediaType(response.headers.get("content-type"));
  if (contentType !== expected.contentType) {
    throw new ApiError(
      response.status,
      `Expected ${expected.contentType}, received ${contentType ?? "no content type"}`,
      null,
    );
  }
  const disposition = response.headers.get("content-disposition");
  const filename = disposition
    ? downloadFilename(disposition, expected.extension)
    : null;
  // The server names the file; inventing one here would let a rename drift
  // apart from what the backend says the download is.
  if (!filename) {
    throw new ApiError(
      response.status,
      "The export response did not include a valid filename",
      null,
    );
  }
  return { blob: await response.blob(), filename };
}

/** Hand the blob to the browser's downloader under the server's filename. */
export function saveDownload(download: DownloadResponse): void {
  const url = URL.createObjectURL(download.blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = download.filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
